import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router";

import { ApiError, api, type Me, type Schemas } from "../api/client";

const MIN_PASSWORD = 12;

export function AcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const invite = useQuery({
    queryKey: ["invite", token],
    queryFn: () =>
      api.get<Schemas["InvitePreviewOut"]>(`/api/auth/invites/${encodeURIComponent(token)}`),
    enabled: token.length > 0,
    retry: false,
  });

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (password.length < MIN_PASSWORD) {
      setError(`Password must be at least ${MIN_PASSWORD} characters`);
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const me = await api.post<Me>("/api/auth/accept-invite", { token, name, password });
      qc.setQueryData(["auth", "me", null], me);
      navigate("/screen", { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not accept the invite");
    } finally {
      setBusy(false);
    }
  }

  const invalid = !token || invite.isError;
  return (
    <div className="flex min-h-screen items-center justify-center bg-zinc-50 px-4 dark:bg-zinc-950">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm space-y-4 rounded-xl border border-zinc-200 bg-white p-6 shadow-sm dark:border-zinc-800 dark:bg-zinc-900"
      >
        <h1 className="text-xl font-semibold">Accept invitation</h1>
        {invalid ? (
          <p role="alert" className="text-sm text-rose-700 dark:text-rose-400">
            This invitation link is invalid or has expired.
          </p>
        ) : (
          <>
            <p className="text-sm text-zinc-600 dark:text-zinc-300">
              {invite.data
                ? `${invite.data.email} — ${invite.data.tenant_name ?? "platform"} (${invite.data.role})`
                : "Checking invitation…"}
            </p>
            <label className="block text-sm">
              <span className="font-medium">Name</span>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
                className="mt-1 w-full rounded-md border border-zinc-300 px-3 py-2 dark:border-zinc-700 dark:bg-zinc-950"
              />
            </label>
            <label className="block text-sm">
              <span className="font-medium">Password (min {MIN_PASSWORD} characters)</span>
              <input
                type="password"
                autoComplete="new-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full rounded-md border border-zinc-300 px-3 py-2 dark:border-zinc-700 dark:bg-zinc-950"
              />
            </label>
            <label className="block text-sm">
              <span className="font-medium">Confirm password</span>
              <input
                type="password"
                autoComplete="new-password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="mt-1 w-full rounded-md border border-zinc-300 px-3 py-2 dark:border-zinc-700 dark:bg-zinc-950"
              />
            </label>
            {error ? (
              <p role="alert" className="text-sm text-rose-700 dark:text-rose-400">
                {error}
              </p>
            ) : null}
            <button
              type="submit"
              disabled={busy || !invite.data}
              className="w-full rounded-md bg-emerald-700 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-800 disabled:opacity-60"
            >
              {busy ? "Creating account…" : "Create account"}
            </button>
          </>
        )}
      </form>
    </div>
  );
}
